import React, { useState, useEffect } from 'react';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Icon from '../../../components/AppIcon';

const EmailComposeModal = ({ isOpen, onClose, recipientEmail, recipientName }) => {
  const [formData, setFormData] = useState({
    to: '',
    subject: '',
    message: ''
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');

  // Pre-fill recipient email when modal opens
  useEffect(() => {
    if (isOpen && recipientEmail) {
      setFormData(prev => ({
        ...prev,
        to: recipientEmail
      }));
      setErrors({});
      setSuccessMessage('');
    }
  }, [isOpen, recipientEmail]);

  const validateForm = () => {
    const newErrors = {};

    if (!formData?.to?.trim()) {
      newErrors.to = 'Recipient email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/?.test(formData?.to)) {
      newErrors.to = 'Invalid email format';
    }

    if (!formData?.subject?.trim()) {
      newErrors.subject = 'Subject is required';
    }

    if (!formData?.message?.trim()) {
      newErrors.message = 'Message is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));

    // Clear error for this field when user starts typing
    if (errors?.[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    setErrors({});

    try {
      // Import the sendCustomEmail function
      const { sendCustomEmail } = await import('../../../services/userService');
      
      const result = await sendCustomEmail(formData);
      
      if (result?.success) {
        setSuccessMessage('Email sent successfully!');
        setTimeout(() => {
          handleClose();
        }, 2000);
      } else {
        throw new Error(result?.error || 'Failed to send email');
      }
    } catch (error) {
      setErrors(prev => ({
        ...prev,
        submit: error?.message || 'Failed to send email'
      }));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setFormData({
      to: '',
      subject: '',
      message: ''
    });
    setErrors({});
    setSuccessMessage('');
    setIsSubmitting(false);
    onClose?.();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
      <div className="bg-card border border-border rounded-lg shadow-xl max-w-lg w-full mx-4 max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-border">
          <div>
            <h2 className="text-xl font-semibold text-foreground">Compose Email</h2>
            {recipientName && (
              <p className="text-sm text-muted-foreground mt-1">To: {recipientName}</p>
            )}
          </div>
          <button
            onClick={handleClose}
            className="text-muted-foreground hover:text-foreground transition-micro"
            disabled={isSubmitting}
          >
            <Icon name="X" size={20} />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {/* Success Message */}
          {successMessage && (
            <div className="p-3 bg-success/10 border border-success rounded-md">
              <div className="flex items-center space-x-2">
                <Icon name="CheckCircle" size={16} className="text-success" />
                <p className="text-sm text-success font-medium">{successMessage}</p>
              </div>
            </div>
          )}

          {/* Recipient Email */}
          <div>
            <label className="block text-sm font-medium text-foreground mb-1">
              To <span className="text-destructive">*</span>
            </label>
            <Input
              type="email"
              value={formData?.to}
              onChange={(e) => handleChange('to', e?.target?.value)}
              placeholder="recipient@example.com"
              error={errors?.to}
              disabled={isSubmitting}
            />
            {errors?.to && (
              <p className="text-sm text-destructive mt-1">{errors?.to}</p>
            )}
          </div>

          {/* Subject */}
          <div>
            <label className="block text-sm font-medium text-foreground mb-1">
              Subject <span className="text-destructive">*</span>
            </label>
            <Input
              type="text"
              value={formData?.subject}
              onChange={(e) => handleChange('subject', e?.target?.value)}
              placeholder="Enter email subject"
              error={errors?.subject}
              disabled={isSubmitting}
            />
            {errors?.subject && (
              <p className="text-sm text-destructive mt-1">{errors?.subject}</p>
            )}
          </div>

          {/* Message */}
          <div>
            <label className="block text-sm font-medium text-foreground mb-1">
              Message <span className="text-destructive">*</span>
            </label>
            <textarea
              value={formData?.message}
              onChange={(e) => handleChange('message', e?.target?.value)}
              placeholder="Enter your message..."
              rows="8"
              className={`w-full p-3 rounded-lg border ${
                errors?.message ? 'border-destructive' : 'border-border'
              } bg-background text-foreground resize-y focus:outline-none focus:ring-2 focus:ring-primary`}
              disabled={isSubmitting}
            />
            {errors?.message && (
              <p className="text-sm text-destructive mt-1">{errors?.message}</p>
            )}
          </div>

          {/* Submit Error */}
          {errors?.submit && (
            <div className="p-3 bg-destructive/10 border border-destructive rounded-md">
              <p className="text-sm text-destructive">{errors?.submit}</p>
            </div>
          )}

          {/* Actions */}
          <div className="flex gap-3 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={handleClose}
              disabled={isSubmitting}
              className="flex-1"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="default"
              disabled={isSubmitting}
              className="flex-1"
              iconName="Send"
              iconPosition="left"
            >
              {isSubmitting ? 'Sending...' : 'Send Email'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EmailComposeModal;